import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AlertCircle, Loader2 } from "lucide-react";
import NumaMascot from "@/components/NumaMascot";
import { llm, normalizeError } from "@/lib/api";
import { clearAuthorisation, pendingAuthorisation } from "@/lib/oauth";
import { toast } from "sonner";

/**
 * Where the provider sends the user back after they authorise.
 *
 * The one-time code is handed to the backend, which swaps it for a key and
 * stores it encrypted; nothing sensitive is kept in the page.
 */
export default function OAuthCallback() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const done = useRef(false);

  useEffect(() => {
    if (done.current) return;
    done.current = true;

    const code = params.get("code");
    const pending = pendingAuthorisation();

    if (!code) {
      setError({ message: "Autorizzazione annullata o non completata." });
      return;
    }
    if (!pending) {
      setError({
        message: "Non risulta una richiesta di collegamento in corso.",
        action: "Riprova dalle impostazioni del modello.",
      });
      return;
    }

    llm
      .oauthExchange(pending.provider, code, pending.verifier)
      .then(() => {
        clearAuthorisation();
        toast.success("Account collegato", { description: "Le funzioni assistite sono attive." });
        navigate("/", { replace: true });
      })
      .catch((err) => {
        clearAuthorisation();
        setError(normalizeError(err));
      });
  }, [params, navigate]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-paper px-6">
      <div className="w-full max-w-sm text-center">
        <div className="mb-5 flex justify-center">
          <NumaMascot size={72} className="numa-mark" variant={0} />
        </div>
        {error ? (
          <>
            <div className="mb-3 flex justify-center">
              <AlertCircle className="h-5 w-5 text-danger" />
            </div>
            <h1 className="font-serif-display text-xl text-ink">Collegamento non riuscito</h1>
            <p className="numa-meta mt-2">{error.message}</p>
            {error.action && <p className="numa-meta mt-1">{error.action}</p>}
            <button
              type="button"
              onClick={() => navigate("/", { replace: true })}
              className="numa-btn numa-btn-primary mt-6 h-9 px-4"
            >
              Torna a NUMA
            </button>
          </>
        ) : (
          <>
            <div className="mb-3 flex justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-brand" />
            </div>
            <h1 className="font-serif-display text-xl text-ink">Collegamento in corso…</h1>
            <p className="numa-meta mt-2">Stiamo completando l&apos;autorizzazione.</p>
          </>
        )}
      </div>
    </div>
  );
}
